/* eslint-disable no-underscore-dangle */
import { useMemo, useCallback, useState, useEffect } from "react";
import { useHistory } from "react-router";
import { Helmet } from "react-helmet";
import { gql, useQuery } from "@apollo/client";

import { ClientType } from "../../../types/clientType";
import Layout from "../../../components/Layout";
import Table from "../../../components/Table";
import { Heading } from "../../../components/Type";
import { ArrowRight } from "../../../components/Icons/general-interface-1";
import Button from "../../../components/Button";
import StatusFilterDropdown from "../../../components/FilterDropdowns/StatusFilterDropdown";
import LocationFilterDropdown from "../../../components/FilterDropdowns/LocationFilterDropdown";
import UnassignedFilterDropdown, { unassignedList } from "../../../components/FilterDropdowns/UnassignedFilterDropdown";
import SearchInput from "../../../components/SearchInput";
import DashboardNav from "./DashboardNav";
import Loader from "../../../components/Loader";
import EmptyView from "../../../components/Table/EmptyView";
import CreateNewUserButton from "./CreateNewUserButton";
import { ClientCell, DateCell, ProfileListCell, StatusCell, StringListCell } from "../../../components/Table/TableCells";
import styles from "./index.module.scss";
import { statusList } from "../../../utils/status";
import { FilterType } from "../../../types/contextType";
import { useStoreContext } from "../../../contexts/StoreProvider";

const GET_CLIENTS = gql`
  query Clients(
    $limit: Int
    $sort: String
    $start: Int
    $where: JSON
    $connectionWhere: JSON
  ) {
    clients(limit: $limit, sort: $sort, start: $start, where: $where) {
      updated_at
      id
      first_name
      last_name
      health_card
      health_card_version
      cep
      client_files {
        id
        status
        updated_at
      }
      sales_reps {
        id
        first_name
        last_name
        locations {
          id
          name
        }
        role {
          name
        }
      }
      authorizers {
        id
        first_name
        last_name
        role {
          name
        }
      }
    }
    clientsConnection(where: $connectionWhere) {
      aggregate {
        count
      }
    }
  }
`;

const GET_LOCATIONS = gql`
  query Locations($limit: Int, $sort: String, $start: Int, $where: JSON) {
    locations(limit: $limit, sort: $sort, start: $start, where: $where) {
      id
      name
    }
  }
`;

type WhereType = {
  [key: string]:
    | string
    | { [key: string]: boolean | string | string[] }[]
    | string[];
};

const AdminDashboard = () => {
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState<FilterType>({status: null, locations: null, unassigned: null});
  const [clientData, setClientData] = useState<{
    clients: ClientType[];
    count: number;
  }>({ clients: [], count: 0 });
  const [search, setSearch] = useState<string | undefined>();
  const history = useHistory();
  const [, setBoundaryError] = useState<Error | null>(null);
  const { state: { filter }, dispatch } = useStoreContext();

  useEffect(() => {
    setFilters(filter);
  }, [filter]);

  const { data, loading, refetch } = useQuery(GET_CLIENTS, {
    onCompleted: (data) => {
      setClientData({
        clients: data.clients,
        count: data.clientsConnection.aggregate.count,
      });
    },
    onError: (error) => {
      setBoundaryError(() => {
        throw error;
      });
    },
  });

  const { data: locationData } = useQuery(GET_LOCATIONS, {
    onError: (error) => {
      setBoundaryError(() => {
        throw error;
      });
    },
  });

  const handleFilterChange = (
    id: string,
    filter?:
      | { value: string; label: string }
      | { value: string; label: string }[]
  ) => {
    const newFilter = {
      [id]: Array.isArray(filter) ? filter.map((f) => f.value) : filter?.value,
    };
    setFilters({ ...filters, ...newFilter });
    dispatch({
      type: "FILTER",
      payload: {...filters, ...newFilter},
    });
  };

  const fetchData = useCallback(
    ({ pageSize, pageIndex, sortBy }) => {
      const variables: {
        limit: number;
        start: number;
        sort?: unknown;
        where?: WhereType;
        connectionWhere?: WhereType;
      } = {
        limit: pageSize,
        start: pageIndex * pageSize,
      };
      if (sortBy.length) {
        if (sortBy[0].id === "status") {
          variables.sort = `client_files.status:${
            sortBy[0].desc ? "DESC" : "ASC"
          }`;
        } else if (sortBy[0].id === "sales_reps") {
          variables.sort = `sales_reps.first_name:${
            sortBy[0].desc ? "DESC" : "ASC"
          }`;
        } else if (sortBy[0].id === "authorizers") {
          variables.sort = `authorizers.first_name:${
            sortBy[0].desc ? "DESC" : "ASC"
          }`;
        } else if (sortBy[0].id === "updated") {
          variables.sort = `client_files.updated_at:${
            sortBy[0].desc ? "DESC" : "ASC"
          }`;
        } else {
          variables.sort = `${sortBy[0].id}:${sortBy[0].desc ? "DESC" : "ASC"}`;
        }
      }

      const where: WhereType = {};
      if (filter.locations || filter.unassigned || filter.status || search) {
        const and: { [key: string]: boolean | string | string[] }[] = [];
        const or: { [key: string]: boolean | string }[] = [];
        if (filter.locations && filter.locations.length) {
          and.push({ "sales_reps.locations_in": filter.locations });
        }
        if (filter.status) {
          and.push({ "client_files.status": filter.status });
        }
        if (filter.unassigned) {
          and.push({ authorizers_null: filter.unassigned === "unassigned" });
        }
        if (search) {
          or.push({ first_name_contains: search });
          or.push({ last_name_contains: search });
        }
        if (and.length) where._and = and;
        if (or.length) where._or = or;
      }

      variables.where = where;
      variables.connectionWhere = where;

      setPageSize(pageSize);
      refetch(variables).then((response) => {
        if (response.data) {
          setClientData({
            clients: response.data.clients,
            count: response.data.clientsConnection.aggregate.count,
          });
        }
      });
    },
    [refetch, filter, search]
  );

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "first_name",
        Cell: ({ row }) => <ClientCell client={row.original} />,
      },
      {
        Header: "Updated",
        accessor: "updated",
        id: "updated",
        Cell: ({ row }) => (
          <DateCell
            value={
              row.original?.client_files?.sort(
                (a, b) =>
                  new Date(b.updated_at).getTime() -
                  new Date(a.updated_at).getTime()
              )?.[0]?.updated_at
            }
          />
        ),
      },
      {
        Header: "Authorizer(s)",
        accessor: "authorizers",
        Cell: ({ value }) => <ProfileListCell data={value} admin />,
      },
      {
        Header: "Motion Team",
        accessor: "sales_reps",
        Cell: ({ value }) => <ProfileListCell data={value} admin />,
      },
      {
        Header: "Location(s)",
        accessor: "locations",
        disableSortBy: true,
        Cell: ({ row }) => {
          const locations: string[] = [];
          row.original.sales_reps.forEach(
            (rep: { locations: { id: string; name: string }[] }) =>
              rep.locations.forEach((loc) => locations.push(loc.name))
          );

          return <StringListCell data={locations} />;
        },
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ row }) => <StatusCell files={row.original.client_files} />,
      },
    ],
    []
  );

  const getCurrentFilters = useMemo(() => {
    const currentFilters = Object.keys(filters)
      .filter((f) => {
        if (filters[f] && typeof filters[f] !== "string") {
          return filters[f].length;
        }
        return !!filters[f];
      })
      .map((filter) => {
        let id;
        if (filter === "locations") {
          id = "locations";
        }
        if (filter === "status") {
          id = "status";
        }
        if (filter === "unassigned") {
          id = "authorizers";
        }
        return { id, value: filters[filter] };
      });

    if (search) {
      currentFilters.push({ id: "first_name", value: search });
    } else {
      currentFilters.filter((f) => f.id !== "first_name");
    }
    return currentFilters;
  }, [filters, search]);

  const pageCount = data ? Math.ceil(data.clientsConnection.aggregate.count / pageSize) : 0;

  const filterLocations = () => {
    return locationData.locations
      .filter(loc => filters.locations?.includes(loc.id))
      .map((loc) => ({value: loc.id, label: loc.name}));
  };

  const handleResetFilters = () => {
    setFilters({locations: null, status: null, unassigned: null});
    dispatch({
      type: "FILTER",
      payload: {locations: null, status: null, unassigned: null},
    });

    setSearch("");
    const input = (document.getElementById("search-filter") as HTMLInputElement);
    if (input) input.value = "";
  };

  return (
    <>
      <Layout>
        <Helmet>
          <title>Motion Home</title>
        </Helmet>
        <div className={styles.headerRow}>
          <Heading size="h3" component="h1" className={styles.heading}>
            Dashboard
          </Heading>
          <Button
            secondary
            onClick={() => history.push("/admin/status-report")}>
              Import Status Report
          </Button>
          <Button
            primary
            icon={<ArrowRight />}
            onClick={() => history.push("/admin/client/new")}
          >
            Create new client file
          </Button>
          <CreateNewUserButton />
        </div>
        <div className={styles.nav}>
          <DashboardNav />
        </div>
        {loading && (
          <Loader heading="Loading client list..." className={styles.loader} />
        )}
        <div className={loading ? styles.tableHide : styles.tableShow}>
          <div className={styles.filterRow}>
            <div className={styles.filterItem}>
              <SearchInput
                onChange={(value) => setSearch(value)}
                placeholder="Search by first or last name"
                loading={loading}
              />
            </div>
            {locationData && (
              <div className={styles.filterItem}>
                <LocationFilterDropdown
                  options={locationData.locations.map(
                    (loc: { id: string; name: string }) => ({
                      value: loc.id,
                      label: loc.name,
                    })
                  )}
                  onChange={(locations: { value: string; label: string }[]) => handleFilterChange("locations", locations) }
                  values={filterLocations()}
                />
              </div>
            )}

            <div className={styles.filterItem}>
              <UnassignedFilterDropdown
                onChange={(unassigned: { value: string; label: string }) => handleFilterChange("unassigned", unassigned) }
                value={unassignedList.find((unassigned) => unassigned.value === filters.unassigned)}
              />
            </div>
            <div className={styles.filterItem}>
              <StatusFilterDropdown
                onChange={(status: { value: string; label: string }) => handleFilterChange("status", status) }
                value={statusList.find((status) => status.value === filters.status)}
                />
            </div>
            <div>
              <Button
                secondary
                onClick={handleResetFilters}
              >
                Reset Filters
              </Button>
            </div>
          </div>
          <Table
            data={clientData.clients}
            columns={columns}
            pageCount={pageCount}
            fetchData={fetchData}
            total={clientData.count}
            loading={loading}
            handleRowClick={(row) => {
              history.push(`/admin/client/${row.original.id}`);
            }}
            emptyView={
              <EmptyView
                message={
                  search ||
                  filters.locations ||
                  filters.status ||
                  filters.unassigned
                    ? `No results found matching your search criteria.`
                    : "You do not have any clients on file yet."
                }
              >
                <Button primary onClick={() => history.push("/client/new")}>
                  Create new client file
                </Button>
              </EmptyView>
            }
            initialState={{ sortBy: [{ id: "first_name", desc: false }] }}
            filters={getCurrentFilters}
          />
        </div>
      </Layout>
    </>
  );
};

export default AdminDashboard;
